<div class="min-h-screen bg-surface">
  <!-- Hero: asymmetric layout with diagonal accent -->
  <section class="relative -mx-4 overflow-hidden border-b border-slate-200 px-4 py-10 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8"
    aria-labelledby="writing-hero-title">
    <div class="absolute inset-0 bg-gradient-to-br from-slate-50 via-sky-50/30 to-blue-50/40"></div>
    <div class="absolute inset-0 bg-[linear-gradient(115deg,transparent_48%,rgba(30,64,175,0.04)_52%,transparent_56%)]">
    </div>
    <!-- Decorative wavy shapes (right side) -->
    <div class="pointer-events-none absolute -right-20 -top-10 h-64 w-64 rounded-full bg-sky-100/40 blur-3xl"></div>
    <div class="pointer-events-none absolute bottom-0 right-0 h-48 w-48 rounded-full bg-blue-100/30 blur-2xl"></div>
    <!-- Sparkles -->
    <div class="pointer-events-none absolute bottom-8 right-16 flex gap-2 opacity-60">
      <span class="h-1 w-1 rounded-full bg-sky-300"></span>
      <span class="h-1.5 w-1.5 rounded-full bg-sky-200"></span>
      <span class="h-1 w-1 rounded-full bg-blue-200"></span>
      <span class="h-1 w-1 rounded-full bg-sky-300"></span>
    </div>

    <div class="relative mx-auto w-[90%]">
      <div class="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between lg:gap-12">
        <!-- Title and subtitle (left) -->
        <div class="pl-20 shrink-0 animate-fade-in-up">
          <h1 id="writing-hero-title" class="font-display text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl">
            <span class="block">TCF Canada 写作</span>
          </h1>
          <p class="mt-3 text-base text-slate-600 sm:text-lg">
            模拟考试界面 · 追踪进度 · 高分表达库 · AI 批改
          </p>
          <p class="mt-1.5 text-base font-semibold text-slate-700 sm:text-lg">
            助力你摆脱9分, 早日上岸
          </p>
          <div class="mt-6 flex flex-wrap gap-4">
            <a routerLink="/writing/topics" [queryParams]="{ mode: 'view' }"
              class="group inline-flex items-center gap-2.5 rounded-xl bg-blue-600 px-5 py-3 text-base font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-600/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
              <svg class="h-5 w-5 transition-transform group-hover:scale-110" fill="none" viewBox="0 0 24 24"
                stroke-width="2" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
              </svg>
              开始练习
            </a>
            <a routerLink="/writing/history"
              class="group inline-flex items-center gap-2.5 rounded-xl border-2 border-slate-300 bg-white px-5 py-3 text-base font-semibold text-slate-700 shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-400 hover:bg-slate-50 hover:shadow-lg hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
              <svg class="h-5 w-5 transition-transform group-hover:scale-110" fill="none" viewBox="0 0 24 24"
                stroke-width="2" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              查看记录
            </a>
          </div>
        </div>

        <!-- CLB cards with staircase + curved arrow (right) -->
        <div class="pr-10 relative min-w-0 flex-1 animate-fade-in-up animate-delay-100 lg:max-w-xl">
          <!-- Curved arrow (SVG) - below cards -->
          <svg class="absolute -bottom-2 left-0 right-0 h-8 w-full text-sky-200" viewBox="0 0 400 40" fill="none"
            aria-hidden="true">
            <path d="M0 35 Q100 5 200 15 T400 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <path d="M385 8 L400 5 L390 15 Z" fill="currentColor" />
          </svg>

          <div class="flex items-end justify-between gap-2 sm:gap-3">
            @for (card of clbCards; track card.level) {
            <div
              class="flex flex-1 flex-col rounded-xl px-3 py-3 shadow-card transition-all duration-300 hover:-translate-y-0.5 hover:shadow-card-hover"
              [style.background]="card.color" [style.min-height]="card.minHeight">
              <span class="mb-2 flex justify-center">
                <span
                  class="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700 shadow-sm">
                  {{ card.cefr }}
                </span>
              </span>
              <div class="flex flex-1 flex-col justify-center text-center">
                <p class="text-sm font-bold sm:text-base" [class.text-slate-800]="!card.textLight"
                  [class.text-white]="card.textLight">
                  {{ card.level }}
                </p>
                <p class="mt-0.5 text-[10px] sm:text-xs" [class.text-slate-700]="!card.textLight"
                  [class.text-white]="card.textLight" [class.text-white\/95]="card.textLight">
                  Score: {{ card.score }}
                </p>
              </div>
            </div>
            }
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Main content -->
  <main class="px-4 py-8 sm:px-6 lg:px-8">
    <div class="mx-auto w-[90%] space-y-8">
      <!-- Tips card -->
      <article class="rounded-xl border border-amber-200 bg-amber-50/60 p-5 shadow-sm" aria-labelledby="tips-title">
        <div class="flex items-start gap-3">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600"
            aria-hidden="true">
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 6.75l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
          </div>
          <div>
            <h3 id="tips-title" class="font-display text-base font-semibold text-slate-900">写作小贴士</h3>
            <ul class="mt-2 space-y-1.5 text-sm text-slate-700">
              @for (tip of writingTips; track tip) {
              <li class="flex gap-2">
                <span class="mt-1 shrink-0 h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true"></span>
                <span>{{ tip }}</span>
              </li>
              }
            </ul>
          </div>
        </div>
      </article>

      <!-- Task banks: clear hierarchy, one 查看题目 button, 3 cards per row -->
      <section aria-labelledby="task-banks-title">
        <div class="mb-6 flex flex-wrap items-baseline justify-between gap-4">
          <div>
            <h2 id="task-banks-title" class="font-display text-xl font-semibold text-slate-900">写作题库</h2>
            <p class="mt-0.5 text-sm text-slate-500">按难度从易到难：任务 1 → 任务 2 → 任务 3</p>
          </div>
          <a routerLink="/writing/topics" [queryParams]="{ mode: 'view' }"
            class="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            查看题目
          </a>
        </div>

        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          @for (card of questionBankCards; track card.id) {
          <article
            class="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-card-hover">
            <!-- Vertical card layout -->
            <div class="flex flex-1 flex-col p-5">
              <div class="flex items-center gap-3">
                <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600"
                  aria-hidden="true">
                  <svg class="h-5 w-5" viewBox="0 0 24 24">
                    <g fill="currentColor" class="text-slate-400">
                      <rect x="2" y="14" width="4" height="6" rx="1" />
                      <rect x="8" y="10" width="4" height="10" rx="1" />
                      <rect x="14" y="6" width="4" height="14" rx="1" />
                      <rect x="20" y="2" width="4" height="18" rx="1" />
                    </g>
                    <g fill="currentColor" class="text-blue-600">
                      <rect x="2" y="14" width="4" height="6" rx="1" />
                      @if (card.id === 'tache2' || card.id === 'tache3') {
                      <rect x="8" y="10" width="4" height="10" rx="1" />
                      }
                      @if (card.id === 'tache3') {
                      <rect x="14" y="6" width="4" height="14" rx="1" />
                      }
                    </g>
                  </svg>
                </div>
                <h3 class="font-display text-base font-semibold text-slate-900">{{ card.title }}</h3>
              </div>

              <p class="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-600">{{ card.taskRequirement }}</p>

              <!-- Exam info: word count & time -->
              <div class="mt-4 flex flex-wrap gap-2">
                <span
                  class="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                  <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                  {{ card.wordCount }}
                </span>
                <span
                  class="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                  <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  建议 {{ card.timeSuggest }}
                </span>
              </div>

              <!-- Progress bar -->
              @if (card.completedCount !== undefined && card.totalCount !== undefined) {
              <div class="mt-4">
                <div class="flex items-center justify-between text-xs text-slate-500">
                  <!-- <span>已完成 {{ card.completedCount }} / {{ card.totalCount }} 题</span> -->
                  <span class="font-medium text-slate-700">{{ getProgressPercent(card) }}%</span>
                </div>
                <div class="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div class="h-full rounded-full bg-blue-500 transition-all duration-300"
                    [style.width.%]="getProgressPercent(card)"></div>
                </div>
              </div>
              }

              <!-- Common topics at bottom -->
              @if (card.commonTopics && card.commonTopics.length) {
              <div class="mt-4 border-t border-slate-100 pt-4">
                <h4 class="text-xs font-semibold text-slate-700">常见话题</h4>
                <div class="mt-2 flex flex-wrap gap-1.5">
                  @for (topic of card.commonTopics; track topic) {
                  <span class="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                    {{ topic }}
                  </span>
                  }
                </div>
              </div>
              }
            </div>
          </article>
          }
        </div>
      </section>
      <div>
        写作评分标准
        <div>
          TODO
        </div>
      </div>
    </div>
  </main>
</div>